/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useState, useEffect } from 'react';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import FormBox from '../../common/FormBox/FormBox';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { EARLIEST_BIRTH_DATE } from '../../../data/constants/constants';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { isSalesPersonRole } from 'data/constants/roles';
import { useTranslation } from 'react-i18next';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import { fetchOptions } from './utils';
import BaseInput from 'components/common/BaseInput';
import { formatNumber } from 'utils/formatting';
import { useSpouseUserId } from 'hooks/useSpouseUserId';
import {
    mergeSpouseUserTables,
    splitSpouseProfilePayload,
    type SpouseProfileFields,
} from 'utils/spouseProfilePayload';

export interface Spouse {
    firstname: string;
    lastname: string;
    middlename: string;
    birth_date: string;
    gender: string;
    nationality: string;
    country_of_residence: string;
    region_of_residence: string;
    residency_status: string;
    residence_since: string;
    income: string;
}

const initSpouseConvertedMsg: Spouse = {
    firstname: '',
    lastname: '',
    middlename: '',
    birth_date: '',
    gender: '',
    nationality: '',
    country_of_residence: '',
    region_of_residence: '',
    residency_status: '',
    residence_since: '',
    income: '',
};

const initSpouse: Spouse[] = [
    {
        firstname: '',
        lastname: '',
        middlename: '',
        birth_date: '',
        gender: '',
        nationality: '',
        country_of_residence: '',
        region_of_residence: '',
        residency_status: '',
        residence_since: '',
        income: '',
    },
];

export default function SpousePartnerInfo() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const [spouseData, setSpouseData] = useState<Spouse[]>(initSpouse);
    const [validationMsgSpouse, setValidationMsgSpouse] = useState<Spouse[]>([
        initSpouseConvertedMsg,
    ]);

    const [isSpouseInfo, setIsSpouseInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (isSpouseInfo) {
            setIsLoading(false);
        }
    }, [isSpouseInfo]);

    const [spouseResidenceStatus, setSpouseResidenceStatus] = useState<string[]>([
        '',
        'Citizen',
        'Permanent resident',
        'Temporary resident',
        'Study permit',
        'Refugee',
    ]);

    const is_spouse_in_destination_country =
        spouseData.length > 0 &&
        ['Australia', 'Canada', 'United Kingdom'].includes(
            spouseData[0].country_of_residence as string
        );

    // SPOUSE update
    const handleUpdateSpouse = async (field: string, value: string | any) => {
        validate(field, value, undefined, 0, 'spouse');
        let temp: any;
        if (field === 'country_of_residence') {
            temp = {
                ...spouseData[0],
                [field]: value,
                region_of_residence: '',
                residency_status: '',
            };
            const res = await api.staticResources.getResidenceStatusList(value);
            setSpouseResidenceStatus(res.data);
        } else {
            temp = { ...spouseData[0], [field]: value };
        }
        setSpouseData([temp]);
    };

    const handleDateUpdateSpouse = (field: string, newDate: Date | null) => {
        if (newDate === null) {
            validate(field, '', undefined, 0, 'spouse');
            const temp_Spouse = [...validationMsgSpouse];
            temp_Spouse[0] = {
                ...temp_Spouse[0],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsgSpouse(temp_Spouse);
            const temp = [...spouseData];
            temp[0] = { ...spouseData[0], [field]: '' };
            setSpouseData(temp);
            return;
        }

        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp_Spouse = [...validationMsgSpouse];
            temp_Spouse[0] = { ...temp_Spouse[0], [field]: 'Invalid date format' };
            setValidationMsgSpouse(temp_Spouse);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep day/month visible
            const temp_Spouse = [...validationMsgSpouse];
            temp_Spouse[0] = { ...temp_Spouse[0], [field]: 'Invalid date format' };
            setValidationMsgSpouse(temp_Spouse);
            const temp = [...spouseData];
            temp[0] = { ...spouseData[0], [field]: extractDateFormat };
            setSpouseData(temp);
            return;
        }

        const temp_Spouse = [...validationMsgSpouse];
        temp_Spouse[0] = { ...temp_Spouse[0], [field]: '' };
        setValidationMsgSpouse(temp_Spouse);
        validate(field, extractDateFormat, undefined, 0, 'spouse');
        const temp = [...spouseData];
        temp[0] = { ...spouseData[0], [field]: extractDateFormat };
        setSpouseData(temp);
    };

    const loadSpouseData = async (
        data: Spouse | Record<string, unknown> | SpouseProfileFields | null
    ) => {
        if (data && Object.keys(data).length) {
            const normalized: Spouse = {
                ...initSpouse[0],
                ...(data as Spouse),
                firstname: (data as Spouse).firstname ?? '',
                lastname: (data as Spouse).lastname ?? '',
                middlename: (data as Spouse).middlename ?? '',
                birth_date: (data as Spouse).birth_date ?? '',
                gender: (data as Spouse).gender ?? '',
                nationality: (data as Spouse).nationality ?? '',
                country_of_residence: (data as Spouse).country_of_residence ?? '',
                region_of_residence: (data as Spouse).region_of_residence ?? '',
                residency_status: (data as Spouse).residency_status ?? '',
                residence_since: (data as Spouse).residence_since ?? '',
                income: String((data as Spouse).income ?? ''),
            };
            const country = normalized.country_of_residence;
            if (country) {
                const res = await api.staticResources.getResidenceStatusList(country);
                setSpouseResidenceStatus(res.data);
            }
            setSpouseData([normalized]);
        }
        setIsSpouseInfo(true);
    };

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);
    const loggedInUserId = useSelector((state: RootState) => state.user.entity.id);
    const completionOwnerId =
        Number.isFinite(candidate_id) && candidate_id > 0 ? candidate_id : loggedInUserId;

    const markSpouseStepComplete = () => {
        if (!Number.isFinite(completionOwnerId) || completionOwnerId <= 0) {
            return;
        }
        void api.completedForms.editCompletedFormStatus(completionOwnerId, { spouse: true });
    };
    const validCandidateId =
        Number.isFinite(candidate_id) && candidate_id > 0 ? candidate_id : undefined;
    const { spouseUserId, isLoading: spouseIdLoading } = useSpouseUserId(validCandidateId);

    const isSalesPerson = isSalesPersonRole(role);

    useEffect(() => {
        if (!isAuthenticated) {
            setIsLoading(false);
            return;
        }
        if (spouseIdLoading) {
            return;
        }
        setIsLoading(true);
        setIsSpouseInfo(false);
        setSpouseData(initSpouse);

        const loadFromUserTables = async (userId: number) => {
            try {
                const [userRes, personalRes, citizenshipRes] = await Promise.all([
                    api.tableInput.getForUser('User', userId),
                    api.tableInput.getForUser('PersonalInfo', userId),
                    api.tableInput.getForUser('Citizenship', userId),
                ]);
                await loadSpouseData(
                    mergeSpouseUserTables(userRes.data, personalRes.data, citizenshipRes.data)
                );
            } catch (error) {
                console.error(error);
                await loadSpouseData(null);
            }
        };

        if (spouseUserId) {
            void loadFromUserTables(spouseUserId);
            return;
        }
        if (isSalesPerson) {
            api.tableInput.fetchCandidateInputSales(loadSpouseData, 'Spouse', candidate_id);
        } else {
            api.tableInput.fetchCandidateInput(loadSpouseData, 'Spouse');
        }
    }, [candidate_id, isAuthenticated, spouseUserId, spouseIdLoading, isSalesPerson]);

    const spouseTables = splitSpouseProfilePayload(spouseData[0] ?? {});
    const spouseTableNames = spouseUserId
        ? spouseTables.citizenships.length > 0
            ? ['User', 'PersonalInfo', 'Citizenship']
            : ['User', 'PersonalInfo']
        : ['Spouse'];
    const spouseTableData = spouseUserId
        ? spouseTables.citizenships.length > 0
            ? [spouseTables.user, spouseTables.personalInfo, spouseTables.citizenships]
            : [spouseTables.user, spouseTables.personalInfo]
        : [spouseData];

    // FIELD VALIDATION
    const validate = async (
        field: string,
        value: string,
        valid?: boolean,
        idx?: number,
        section?: string
    ) => {
        let validated = true;

        //Spouse
        const temp_Spouse = [...validationMsgSpouse];
        if (!field && !value) {
            for (let i = 0; i < spouseData.length; i++) {
                for (const key of Object.keys(initSpouse[0])) {
                    const value = spouseData[i][key as keyof Spouse];
                    let msg = '';
                    if (key === 'region_of_residence' && !is_spouse_in_destination_country)
                        continue;
                    if (key === 'residence_since') continue;
                    if (key === 'middlename') continue;
                    if (key === 'income') continue;
                    if (key === 'residency_status' && !is_spouse_in_destination_country) continue;
                    if (key === 'firstname' && section === 'spouse') {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        temp_Spouse[i] = { ...temp_Spouse[i], [key]: msg };
                        setValidationMsgSpouse(temp_Spouse);
                    } else if (key !== 'gender' && section === undefined) {
                        if (value === '' || value === null) {
                            msg = t('requiredField', { lng: language });
                            validated = false;
                            temp_Spouse[i] = { ...temp_Spouse[i], [key]: msg };
                            setValidationMsgSpouse(temp_Spouse);
                        }
                    }
                }
            }
        } else {
            let msg = '';
            if (field === 'firstname' && section === 'spouse' && (value === '' || value === null)) {
                msg = t('requiredField', { lng: language });
                validated = false;
                temp_Spouse[idx!] = { ...temp_Spouse[idx!], [field]: msg };
                setValidationMsgSpouse(temp_Spouse);
            } else if (value === '' || value === null) {
                msg = t('requiredField', { lng: language });
                validated = false;
            }
            temp_Spouse[idx!] = { ...temp_Spouse[idx!], [field]: msg };
        }
        setValidationMsgSpouse(temp_Spouse);

        return validated;
    };

    const validateNumberMe = (field: string, value: string | null) => {
        let validated = true;
        const temp_Spouse = [...validationMsgSpouse];
        let msg = '';
        if (value && isNaN(Number(value.replaceAll(',', '')))) {
            msg = t('shouldBeANumber', { lng: language });
            validated = false;
        }

        temp_Spouse[0] = { ...temp_Spouse[0], [field]: msg };
        console.log('lol', temp_Spouse[0]);
        setValidationMsgSpouse(temp_Spouse);
        return validated;
    };

    const handleNumberUpdateMe = (field: string, value: string) => {
        if (validateNumberMe(field, value)) {
            value = value ? value : '0';
            const valueWithoutSeparators = value
                .replaceAll(' ', '')
                .replaceAll('.', '')
                .split(',')
                .join('');

            const temp = [...spouseData];
            temp[0] = { ...spouseData[0], [field]: valueWithoutSeparators };
            setSpouseData(temp);
        }
    };

    const [nationalities, setNationalities] = useState<string[]>([]);
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [regionOptions, setRegionOptions] = useState({} as { [key: string]: string[] });

    const getNationalityOptions = () =>
        fetchOptions(api?.assessment_options.getNationalityOptions, setNationalities);
    const getCountryOptions = () =>
        fetchOptions(api?.assessment_options.getCountryOptions, setCountryOptions);
    const getRegionOptions = () =>
        fetchOptions(api?.assessment_options.getRegionOptions, setRegionOptions);

    useEffect(() => {
        getNationalityOptions();
        getCountryOptions();
        getRegionOptions();
    }, []);

    const nationalityOptions = nationalities.length > 0 ? nationalities : countryOptions;

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox
                    validation={validate}
                    tableNames={spouseTableNames}
                    data={spouseTableData}
                    targetUserId={spouseUserId}
                    onAfterSave={markSpouseStepComplete}
                >
                    <S.Title>{t('spouseOrCommonLawPartnerInfo', { lng: language })}</S.Title>
                    <S.CardContent style={{ marginTop: '0px' }}>
                        <S.GridSection>
                            <ToggleButtonGroup
                                options={['Male', 'Female']}
                                name="gender"
                                label={t('gender', { lng: language })}
                                value={spouseData[0].gender!}
                                onChange={handleUpdateSpouse}
                                error={validationMsgSpouse[0].gender}
                            />
                            <TextField
                                id="firstname"
                                label={`*${t('firstName', { lng: language })}`}
                                type="text"
                                value={spouseData[0].firstname}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('firstname', e.target.value, undefined, 0, 'spouse');
                                }}
                                onChange={(e) => {
                                    handleUpdateSpouse('firstname', e.target.value);
                                }}
                                error={validationMsgSpouse[0]?.firstname !== ''}
                                helperText={validationMsgSpouse[0]?.firstname}
                            />

                            <TextField
                                id="lastname"
                                label={`*${t('lastName', { lng: language })}`}
                                type="text"
                                value={spouseData[0].lastname}
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('lastname', e.target.value, undefined, 0, 'spouse');
                                }}
                                onChange={(e) => {
                                    handleUpdateSpouse('lastname', e.target.value);
                                }}
                                error={validationMsgSpouse[0]?.lastname !== ''}
                                helperText={validationMsgSpouse[0]?.lastname}
                            />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    minDate={new Date(EARLIEST_BIRTH_DATE)}
                                    maxDate={new Date()}
                                    label={`*${t('dateOfBirth', { lng: language })}`}
                                    format="dd/MM/yyyy"
                                    value={parseStoredDate(spouseData[0].birth_date)}
                                    onChange={(date: Date | null) => {
                                        handleDateUpdateSpouse('birth_date', date);
                                    }}
                                    slotProps={{
                                        field: { clearable: true },
                                        textField: {
                                            error: validationMsgSpouse[0]?.birth_date !== '',
                                            helperText: validationMsgSpouse[0]?.birth_date,
                                            title: t('payAttentionToFormat', { lng: language }),
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                            <Autocomplete
                                disablePortal
                                value={spouseData[0].nationality}
                                id="nationality"
                                options={nationalityOptions}
                                onChange={(_, value: string | null) =>
                                    handleUpdateSpouse('nationality', value)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate('nationality', e.target.value, undefined, 0, 'spouse');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('nationality', { lng: language })}`}
                                        error={validationMsgSpouse[0]?.nationality !== ''}
                                        helperText={validationMsgSpouse[0]?.nationality}
                                    />
                                )}
                            />
                            <Autocomplete
                                disablePortal
                                value={spouseData[0].country_of_residence}
                                id="residence"
                                options={countryOptions}
                                onChange={(_, value: string | null) =>
                                    handleUpdateSpouse('country_of_residence', value)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate(
                                        'country_of_residence',
                                        e.target.value,
                                        undefined,
                                        0,
                                        'spouse'
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('countryOfResidence', { lng: language })}`}
                                        error={validationMsgSpouse[0]?.country_of_residence !== ''}
                                        helperText={validationMsgSpouse[0]?.country_of_residence}
                                    />
                                )}
                            />
                            <Autocomplete
                                hidden={!is_spouse_in_destination_country}
                                disablePortal
                                id="region"
                                value={spouseData[0].region_of_residence}
                                options={regionOptions[spouseData[0].country_of_residence] || []}
                                onChange={(_, v: string | null) =>
                                    handleUpdateSpouse('region_of_residence', v)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate(
                                        'region_of_residence',
                                        e.target.value,
                                        undefined,
                                        0,
                                        'spouse'
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('regionOfResidence', { lng: language })}`}
                                        error={validationMsgSpouse[0]?.region_of_residence !== ''}
                                        helperText={validationMsgSpouse[0]?.region_of_residence}
                                    />
                                )}
                            />
                            <Autocomplete
                                hidden={!is_spouse_in_destination_country}
                                disablePortal
                                id="residency_status"
                                value={spouseData[0].residency_status}
                                options={spouseResidenceStatus}
                                onChange={(_, v: string | null) =>
                                    handleUpdateSpouse('residency_status', v)
                                }
                                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                    validate(
                                        'residency_status',
                                        e.target.value,
                                        undefined,
                                        0,
                                        'spouse'
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`*${t('statusInCountryOfResidence', {
                                            lng: language,
                                        })}`}
                                        error={validationMsgSpouse[0]?.residency_status !== ''}
                                        helperText={validationMsgSpouse[0]?.residency_status}
                                    />
                                )}
                            />
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    views={['year', 'month']}
                                    minDate={new Date(EARLIEST_BIRTH_DATE)}
                                    maxDate={new Date()}
                                    label={t('residenceStatusSince', { lng: language })}
                                    format="MM/yyyy"
                                    value={parseStoredDate(spouseData[0].residence_since)}
                                    onChange={(date: Date | null) => {
                                        handleDateUpdateSpouse('residence_since', date);
                                    }}
                                    slotProps={{
                                        field: { clearable: true },
                                        textField: {
                                            error: validationMsgSpouse[0].residence_since !== '',
                                            helperText: validationMsgSpouse[0].residence_since,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <BaseInput
                                id="income"
                                label={`${t('approxYearlyIncome(USD)', { lng: language })}`}
                                type="text"
                                value={formatNumber(
                                    spouseData[0].income ? spouseData[0].income : null
                                )}
                                onFocus={(e) => {
                                    validate(
                                        'income',
                                        e.target.value,
                                        undefined,
                                        undefined,
                                        'personalInfo'
                                    );
                                }}
                                onChange={(e) => {
                                    handleNumberUpdateMe('income', e.target.value);
                                }}
                                error={validationMsgSpouse[0]?.income !== ''}
                                helperText={validationMsgSpouse[0]?.income}
                            />
                        </S.GridSection>
                    </S.CardContent>
                    <S.ContentDivider style={{ marginBottom: '40px !important' }} />
                </FormBox>
            )}
        </>
    );
}
